import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import IndividualAvatar from "marketplace/view/individual-avatar";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters
} from "users/values/user-coworker/api/request-contracts/query-user-coworker-api-request";
import UserCoworkerApiService from "users/values/user-coworker/api/user-coworker-api-service";
import UserCoworker from "users/values/user-coworker/user-coworker";

const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type UserCoworkersProps = {
  selectedMemberUserIds?: Guid[];
  onSelected: (rowData: UserCoworker) => void;
};

export default function UserCoworkers(
  props: Readonly<UserCoworkersProps>
) {
  const {
    selectedMemberUserIds,
    onSelected
  } = props;

  const columnHelper = createMRTColumnHelper<UserCoworker>();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [errorLoading, setErrorLoading] = React.useState(false);
  const [data, setData] = React.useState<UserCoworker[]>([]);
  const [rowCount, setRowCount] = React.useState(0);

  // Pagination state
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const session = useSession();

  function renderNameColumn(userCoworker: UserCoworker) {
    if (!userCoworker.name) return <Icon>remove</Icon>;
    return (
      <NameCellContainer>
        <IndividualAvatar
          avatarId={userCoworker.avatarId}
          individualId={userCoworker.individualId}
          session={session}
        />
        <RowTitle>{userCoworker.name.toString()}</RowTitle>
      </NameCellContainer>
    );
  }

  function renderEmailColumn(userCoworker: UserCoworker) {
    if (!userCoworker.email) return <Icon>remove</Icon>;
    const email = userCoworker.email.toString();
    return <Link href={`mailto:${email}`}>{email}</Link>;
  }

  const columns = [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor(renderEmailColumn, {
      header: "Email",
      id: "email",
    }),
  ];

  useEffect(() => {
    let abortController = new AbortController();
    void fetchData(abortController);
    return () => {
      abortController.abort();
    };
  }, [pageIndex, pageSize, session]);

  const fetchData = async (abortController: AbortController) => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    try {
      const userCoworkerService = new UserCoworkerApiService(session);
      const userCoworkerFilter = new UserCoworkerFilterParameters();
      const userCoworkerOrder = new UserCoworkerOrderParameters(
        UserCoworkerOrderField.Name,
        OrderDirection.Ascending
      );
      
      const userCoworkerPagination = new PaginationParameters(pageIndex, pageSize);
      const userCoworkerQuery = new UserCoworkerQuery(
        userCoworkerFilter,
        userCoworkerOrder,
        userCoworkerPagination
      );

      const coworkers = await userCoworkerService.getUserCoworkers(
        userCoworkerQuery,
        abortController
      );

      setData(coworkers.data);
      setRowCount(coworkers.totalElements);
    } catch (error) {
      if (error instanceof CanceledError) return;
      setErrorLoading(true);
      console.error(error);
      return;
    } finally {
      setErrorLoading(false);
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => {
      const disabled = selectedMemberUserIds?.some(
        (id) => row.original?.userId && id.isEqualTo(row.original.userId)
      );
      return {
        onClick: disabled ? undefined : () => onSelected(row.original),
        sx: {
          pointerEvents: disabled ? "none" : "auto",
          cursor: disabled ? "default" : "pointer",
        },
      };
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: errorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    rowCount,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const { pageIndex: newPageIndex, pageSize: newPageSize } = updater({ pageIndex, pageSize });
        setPageIndex(newPageIndex);
        setPageSize(newPageSize);
      } else {
        setPageIndex(updater.pageIndex);
        setPageSize(updater.pageSize);
      }
    },
    state: {
      isLoading,
      pagination: { pageIndex, pageSize },
      showAlertBanner: errorLoading,
      showProgressBars: isRefetching,
    },
  });

  return <MaterialReactTable table={table} />;
}
